<template>
    <div class="login w-100 h-100 d-flex">
        <div class="login-rail d-none d-md-flex flex-column justify-content-between p-5">
            <div class="d-flex align-items-center">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" class="me-2"><circle cx="6" cy="17" r="3"/><circle cx="18" cy="17" r="3"/><path d="M6 17 10 8h5l3 9M10 8 8 4H5"/></svg>
                <span class="login-rail-brand">VueltaCiclista</span>
            </div>
            <div>
                <div class="login-rail-title">Bienvenido<br>de nuevo</div>
                <div class="login-rail-rule"></div>
            </div>
            <div class="login-rail-footer">Entra con tu cuenta</div>
        </div>

        <div class="flex-fill d-flex justify-content-center align-items-center p-3 overflow-auto">
            <div class="login-card">
                <div class="mb-4">
                    <h1 class="login-card-title">Inicia sesión</h1>
                    <div class="login-card-subtitle">{{ identifier_label_hint }}</div>
                </div>

                <div class="mb-3">
                    <label class="login-field-label">{{ identifier_label }}</label>
                    <input type="text" class="form-control" id="login-email" v-model="email" :placeholder="identifier_placeholder" :disabled="submitting"/>
                </div>
                <div class="mb-4">
                    <label class="login-field-label">Contraseña</label>
                    <input type="password" class="form-control" id="login-password" v-model="password" placeholder="Contraseña" :disabled="submitting"/>
                </div>

                <button type="button" class="btn btn-primary w-100" :disabled="submitting" @click="login">
                    <span v-if="submitting" class="spinner-border spinner-border-sm me-2"></span>{{ submitting ? 'Entrando...' : 'Entrar' }}
                </button>

                <div class="login-back mt-4 text-center">
                    <a href="#" @click.prevent="$router.push({name: 'Register'})">¿No tienes cuenta? Regístrate</a>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import utils from '@/../../shared/utils'
import api from '@/api'

export default {
    name: 'Login',
    data(){
        return {
            utils: utils,
            email: '',
            password: '',
            submitting: false,
        };
    },
    computed: {
        // Event-mode staff accounts (seed_users.js) only ever get a
        // username+password, never an email - client_login (server) already
        // accepts either identifier, this just decides which one to send
        // based on what was typed. Online mode's REST login is email-only by
        // design (every online account is created via /api/register with a
        // real email), so this only really matters for event mode - kept
        // available either way since it's harmless.
        identifier_is_email(){
            return this.email.includes('@');
        },
        identifier_label(){
            return window.MODE == utils.APP_MODES.ONLINE ? 'Email' : 'Email o usuario';
        },
        identifier_label_hint(){
            return window.MODE == utils.APP_MODES.ONLINE ? 'Entra con tu email' : 'Entra con tu email o tu usuario';
        },
        identifier_placeholder(){
            return window.MODE == utils.APP_MODES.ONLINE ? 'tucorreo@ejemplo.com' : 'tucorreo@ejemplo.com o tu usuario';
        }
    },
    watch:{
        // Event mode only: CLIENT_LOGIN carries no direct reply - success
        // shows up as the user's role flipping away from GUEST on the next
        // CLIENT_UPDATE (see client.js's client_update), failure as a
        // CLIENT_TOAST the app already displays globally. Only navigate for
        // PLAYER here - staff roles (ADMINISTRATOR/ASSISTANT/MANAGER) are
        // already routed to Panel/Home automatically by client.js's
        // handle_client(). Online mode never assigns PLAYER over the
        // websocket (see login() below), so this simply never fires for an
        // online player - harmless to leave active for both modes.
        '$store.state.game.user.role': function(role){
            if(this.submitting && role == utils.USER_ROLES.PLAYER)
                this.$router.push({name: 'MySeasons'});
        }
    },
    mounted(){
        // Online mode: a valid resumed session (see main.js's
        // ensure_online_identity_checked) means this player is already
        // logged in - skip the form.
        if(window.MODE == utils.APP_MODES.ONLINE && this.$store.state.online_user){
            this.redirect_after_login();
            return;
        }

        $('#login-email').on("keyup", function(evt){
            if(evt.key === "Enter") this.login();
        }.bind(this));
        $('#login-password').on("keyup", function(evt){
            if(evt.key === "Enter") this.login();
        }.bind(this));
    },
    methods: {
        // Online PLAYER login always used to land flat on MySeasons - this
        // instead honors a season code scanned via JoinSeason.vue just
        // before this login/registration (see store's
        // pending_join_season_code), joining that specific season and
        // dropping the player straight into it. Falls back to the normal
        // MySeasons picker whenever there's no pending code, or it turned
        // out to be stale (e.g. the season's code got regenerated meanwhile).
        async redirect_after_login(){
            const pending_code = this.$store.state.pending_join_season_code;
            if(pending_code){
                try{
                    const result = await api.join_season(pending_code);
                    this.$store.commit('setPendingJoinSeasonCode', null);
                    window.toast.success('¡Te has unido a ' + result.season.name + '!');
                    this.$router.push({name: 'MyStages', params: {season_id: result.season.id}});
                    return;
                }catch(ex){
                    this.$store.commit('setPendingJoinSeasonCode', null);
                    window.toast.error('El código de invitación ya no es válido.');
                }
            }
            this.$router.push({name: 'MySeasons'});
        },
        async login(){
            if(!this.email || !this.password){
                window.toast.error("Completa los campos.");
                return;
            }

            this.submitting = true;

            if(window.MODE == utils.APP_MODES.ONLINE){
                try{
                    const result = await api.login(this.email, this.password);
                    this.$store.commit("setOnlineUser", result.user);

                    if(result.user.role == utils.USER_ROLES.PLAYER){
                        await this.redirect_after_login();
                    }else{
                        // Staff logging in from an online-mode deployment -
                        // Panel is still entirely websocket-driven (out of
                        // scope to rewrite here), so bridge into the
                        // existing WS login too; client.js's handle_client()
                        // then routes to Panel on its own once it sees the
                        // resulting CLIENT_UPDATE.
                        window.network.wso.send(JSON.stringify({
                            action: window.RPC.CLIENT_LOGIN,
                            params: { email: this.email, password: this.password, device: window.device }
                        }));
                    }
                }catch(ex){
                    window.toast.error(ex.message || "No se ha podido iniciar sesión.");
                }finally{
                    this.submitting = false;
                }
                return;
            }

            window.network.wso.send(
                JSON.stringify({
                    action: window.RPC.CLIENT_LOGIN,
                    params: Object.assign(
                        { password: this.password, device: window.device, event_code: String(this.$route.params.event_code || '').trim() },
                        this.identifier_is_email ? { email: this.email } : { username: this.email }
                    )
                })
            );

            // No ack on failure beyond the CLIENT_TOAST the app already shows -
            // release the button so a mistyped password can be retried.
            setTimeout(() => { this.submitting = false; }, 4000);
        }
    }
}
</script>

<style scoped>
    .login-rail{
        width: 340px;
        flex-shrink: 0;
        background: var(--vc-ink-900);
        color: var(--vc-paper-50);
    }

    .login-rail-brand{
        font-family: "Archivo", sans-serif;
        font-weight: 700;
        font-size: 0.9rem;
        letter-spacing: 0.04em;
    }

    .login-rail-title{
        font-family: "Archivo", sans-serif;
        font-weight: 800;
        font-size: 1.9rem;
        text-transform: uppercase;
        line-height: 1.15;
    }

    .login-rail-rule{
        height: 3px;
        width: 60px;
        background: var(--vc-accent-500);
        margin-top: 20px;
    }

    .login-rail-footer{
        font-size: 0.8rem;
        color: rgba(255,255,255,0.5);
    }

    .login-card{
        width: 100%;
        max-width: 380px;
    }

    .login-card-title{
        font-size: 1.4rem;
    }

    .login-card-subtitle{
        color: var(--vc-text-600);
        font-size: 0.9rem;
        margin-top: 0.3rem;
    }

    .login-field-label{
        font-family: "Archivo", sans-serif;
        font-size: 0.7rem;
        font-weight: 700;
        text-transform: uppercase;
        letter-spacing: 0.06em;
        color: var(--vc-text-600);
        margin-bottom: 0.4rem;
        display: block;
    }

    .login-back{
        font-size: 0.85rem;
    }
</style>
